import { DateTime } from 'luxon'

import { PagedResult } from '../DTOs/Common/PagedResult'
import type { DiggingLoadingSummaryDto } from '../DTOs/Production/DiggingLoadingSummaryDto'
import { DiggingLocationDTO } from '../DTOs/Production/DiggingLocationDTO'
import { StoppageCalc } from '../DTOs/Stoppage/StoppageCalc'
import { queryClient } from '../queryClient'
import { sortCachedRows } from './filterCachedRows'

// Note: the page's own result type - a PagedResult with the shift's totals alongside the rows.
type SummaryResult = PagedResult<DiggingLoadingSummaryDto> & { totalLoads?: number; totalTonnes?: number }

function summaryKey(shiftDate: DateTime, shift: string): unknown[] {
  return ['dataTable', 'chatree', 'diggingLoadingSummary', shiftDate.toISODate(), shift, 'all', false, null]
}

function machineName(plantId: number): string {
  for (const [, page] of queryClient.getQueriesData<PagedResult<StoppageCalc>>({
    queryKey: ['dataTable', 'operations', 'stoppageCalcs'],
  })) {
    const match = (page?.data ?? []).find(row => row?.plantId === plantId)
    if (match?.machineName) return match.machineName
  }
  return ''
}

// Note: the Production list is a server-side aggregate of digging entries and their loads, so it
// cannot be recalculated offline. A digging location created offline therefore adds its own row, with
// no loads yet - otherwise the page reports no digging entries at all for a shift the crew has just
// set up, whether the location was carried over by a shift completion or entered by hand.
export function appendCachedDiggingSummaryRow(
  location: DiggingLocationDTO,
  shiftDate: DateTime,
  shift: string,
  diggerId: number,
): void {
  const key = summaryKey(shiftDate, shift)
  const cached = queryClient.getQueryData<SummaryResult>(key)
  const rows = cached?.data ?? []

  if (rows.some(row => row.diggingPatternId === location.diggingPatternId)) return

  const next: DiggingLoadingSummaryDto[] = [
    ...rows,
    {
      diggingId: diggerId,
      diggingPatternId: location.diggingPatternId,
      plantId: diggerId,
      excavator: machineName(diggerId),
      pitStage: location.pitStage,
      blastMasterId: String(location.blastMasterId ?? ''),
      startTime: location.startTime,
      loads: 0,
      tonnes: 0,
    },
  ]

  writeSummary(key, cached, next)
}

// Note: keep everything the cached result carries and replace only the rows. Building a fresh
// PagedResult dropped totalLoads and totalTonnes, which the page's footer reads through selectMeta -
// so Total Loads and Total Tonnes rendered blank as soon as anything was written here.
// Note: the endpoint orders these by machine name (ShiftRepository), and rows appended offline arrive
// in whatever order the caches were walked in - the list read as jumbled next to the same list online.
function writeSummary(
  key: unknown[],
  cached: SummaryResult | undefined,
  rows: DiggingLoadingSummaryDto[],
  totals?: { loads: number; tonnes: number },
): void {
  const ordered = sortCachedRows(rows, {
    accessors: {},
    fallback: row => row.excavator,
  })

  queryClient.setQueryData(key, {
    ...(cached ?? new PagedResult<DiggingLoadingSummaryDto>([], 0, 1, 1, 1)),
    data: ordered,
    totalCount: ordered.length,
    pageNumber: 1,
    pageSize: Math.max(ordered.length, 1),
    totalLoads: Math.max(0, (cached?.totalLoads ?? 0) + (totals?.loads ?? 0)),
    totalTonnes: Math.max(0, (cached?.totalTonnes ?? 0) + (totals?.tonnes ?? 0)),
  })
}

// Note: Loads and Tonnes are a server-side aggregate over the loads posted against each digging
// entry, so offline they simply stayed at whatever the cache last held - a crew posting loads all
// shift saw zero against every digger and no way to tell whether their entries had registered. The
// queued loads are known here, so add them to the row as they are posted. Tonnes only moves when the
// load carries a tonnage (the server derives it from the truck otherwise), so a shift entered offline
// can show loads counted and tonnes still blank; that is a truer picture than showing neither.
export function addQueuedLoadToCachedDiggingSummary(
  load: { diggingPatternId: number; loads: number; tonnes?: number },
  shiftDate: DateTime,
  shift: string,
  sign: 1 | -1 = 1,
): void {
  // Note: loads may be negative here - an edit passes the difference, not the new value.
  const key = summaryKey(shiftDate, shift)
  const cached = queryClient.getQueryData<SummaryResult>(key)
  const rows = cached?.data
  if (!rows?.some(row => row.diggingPatternId === load.diggingPatternId)) return

  writeSummary(key, cached, rows.map(row => row.diggingPatternId === load.diggingPatternId
    ? {
      ...row,
      loads: Math.max(0, (row.loads ?? 0) + sign * (load.loads ?? 0)),
      tonnes: Math.max(0, (row.tonnes ?? 0) + sign * (load.tonnes ?? 0)),
    }
    : row), { loads: sign * (load.loads ?? 0), tonnes: sign * (load.tonnes ?? 0) })
}

// Note: a shift's total loads from the rows already cached for a list. These totals are server-side
// aggregates, so offline they came back as nothing at all - a screen showing 89 loads of rehandle with
// "Total Loads: 0" underneath it. Summing what is cached is the honest figure: it is exactly the rows
// the crew can see, and the server's own total takes over as soon as the queue syncs.
export function sumCachedLoads(listKey: unknown[]): number {
  const cached = queryClient.getQueryData<{ data?: { loads?: number }[] }>(listKey)
  return (cached?.data ?? []).reduce((total, row) => total + (row?.loads ?? 0), 0)
}
